import React, { useState, useMemo, useEffect } from "react";
import { getAppointmentOwnerName, isOtherEventAppointment } from "../../utils/appointmentOwner";
import { tWithKeyAttr as t } from "../../../../i18nHelper";
import i18n from "../../../../i18n";
import styles from "../../appointmentsPage.module.css";
import { IAppointment, IWeekDay } from "../../types/appointments.types";
import { useAppointmentsContext } from "../../context/AppointmentsContext";
import AppointmentDetailsModal from "../AppointmentDetailsModal/AppointmentDetailsModal";
import {
  getAppointmentColors,
  truncateText,
} from "../../utils/fundingSourceColors";
import {
  getInitialDateForView,
  saveDateForView,
} from "../../utils/calendarStorage";
import CreateAppointmentModal from "../CreateAppointmentModal/CreateAppointmentModal";
import {
  calculateVerticalOffset,
  calculateHeight,
  detectOverlaps,
  calculateHorizontalLayout,
  getContainingHourSlot,
  AppointmentPosition,
} from "../../utils/timeUtils";
import ExportTableButtons from "../../../../components/exportTableButtons/ExportTableButtons";
import {
  buildAppointmentsExport,
  buildSearchFilterSummary,
} from "../../utils/buildAppointmentsExport";

// Get locale based on current language
const getLocale = () => (i18n.language === "he" ? "he-IL" : "en-US");

/**
 * Weekly View Component - Client Version (Read-Only)
 *
 * Displays appointments in a weekly calendar grid.
 * Simplified read-only version without drag-and-drop or editing features.
 */
const WeeklyView: React.FC = () => {
  const [weekStart, setWeekStart] = useState(() =>
    getInitialDateForView("weekly"),
  );
  const [selectedAppointment, setSelectedAppointment] =
    useState<IAppointment | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  // Time Slot Modal State
  const [showTimeSlotModal, setShowTimeSlotModal] = useState(false);
  const [selectedTimeSlot, setSelectedTimeSlot] = useState<{
    date: string;
    time: string;
    appointments: IAppointment[];
  } | null>(null);

  /* Create Appointment State */
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [createModalDate, setCreateModalDate] = useState("");
  const [createModalTime, setCreateModalTime] = useState("");

  // Use appointments data hook

  const {
    searchedAppointments, // Use searched appointments
    searchTerm,
    canSelfBook,
  } = useAppointmentsContext();

  // Persist date when it changes (for navigation back)
  useEffect(() => {
    saveDateForView("weekly", weekStart);
  }, [weekStart]);

  // Get week range for filtering
  const weekEnd = useMemo(() => {
    const end = new Date(weekStart);
    end.setDate(end.getDate() + 6);
    end.setHours(23, 59, 59, 999);
    return end;
  }, [weekStart]);

  // Filter appointments for the week locally from searched appointments
  const weeklyAppointments = useMemo(() => {
    // Manual filter on searchedAppointments
    const startStr = weekStart.toISOString().split("T")[0];
    const endStr = weekEnd.toISOString().split("T")[0];

    return searchedAppointments.filter(
      (app) => app.date >= startStr && app.date <= endStr,
    );
  }, [searchedAppointments, weekStart, weekEnd]);

  const handleCreateSuccess = () => {
    setShowCreateModal(false);
    // Note: In client app, we rely on context refresh or SSE
    // Since this view reads from context, we might need a way to trigger refresh
    // For now assuming parent handles refresh or optimistic UI isn't strictly required
  };

  // Expandable Hours State
  const [expandedHours, setExpandedHours] = useState<Record<string, boolean>>(
    {},
  );

  // Generate hourly time slots from 07:00 to 22:00
  const hourlySlots = useMemo(() => {
    const slots: string[] = [];
    for (let hour = 7; hour <= 22; hour++) {
      slots.push(`${hour.toString().padStart(2, "0")}:00`);
    }
    return slots;
  }, []);

  // Get 15-minute sub-slots for a specific hour (e.g., "08" => ["08:15", "08:30", "08:45"])
  const getSubSlots = (hour: string): string[] => {
    return ["15", "30", "45"].map((minute) => `${hour}:${minute}`);
  };

  // Toggle hour expansion
  const toggleHour = (hour: string) => {
    setExpandedHours((prev) => ({
      ...prev,
      [hour]: !prev[hour],
    }));
  };

  // Toggle all hours at once
  const toggleAllHours = () => {
    const allExpanded = hourlySlots.every((slot) => {
      const [hourStr] = slot.split(":");
      return expandedHours[hourStr];
    });

    if (allExpanded) {
      // Collapse all
      setExpandedHours({});
    } else {
      // Expand all
      const newExpanded: Record<string, boolean> = {};
      hourlySlots.forEach((slot) => {
        const [hourStr] = slot.split(":");
        newExpanded[hourStr] = true;
      });
      setExpandedHours(newExpanded);
    }
  };

  // Generate tooltip text for appointment hover
  const getAppointmentTooltip = (apt: IAppointment): string => {
    const isGoogleEvent = apt.source === "google_calendar";
    const isOther = isOtherEventAppointment(apt, isGoogleEvent);

    let parts: string[] = [];

    // Time
    if (apt.time) {
      let timeText = apt.time;
      if (apt.endTime) {
        timeText += ` - ${apt.endTime}`;
      } else if (apt.duration) {
        // Calculate end time if missing
        const [hours, minutes] = apt.time.split(":").map(Number);
        const endMinutes = hours * 60 + minutes + apt.duration;
        const endH = Math.floor(endMinutes / 60) % 24;
        const endM = endMinutes % 60;
        timeText += ` - ${endH.toString().padStart(2, "0")}:${endM.toString().padStart(2, "0")}`;
      }
      parts.push(timeText);
    }

    // Title/Content
    if (isOther) {
      parts.push(`📝 ${apt.comments || t("OTHER") || "אחר"}`);
    } else if (apt.treatmentGroupName) {
      parts.push(apt.treatmentGroupName);
    }

    // Therapist
    if (!isOther && getAppointmentOwnerName(apt)) {
      parts.push(getAppointmentOwnerName(apt));
    }

    return parts.join(" | ");
  };

  const dayNames = [
    t("SUNDAY"),
    t("MONDAY"),
    t("TUESDAY"),
    t("WEDNESDAY"),
    t("THURSDAY"),
    t("FRIDAY"),
    t("SATURDAY"),
  ];

  const weekDays: IWeekDay[] = useMemo(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    return Array.from({ length: 7 }, (_, i) => {
      const date = new Date(weekStart);
      date.setDate(date.getDate() + i);

      // Fix for local date string YYYY-MM-DD
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const day = String(date.getDate()).padStart(2, "0");
      const localDateStr = `${year}-${month}-${day}`;

      return {
        date: localDateStr,
        dayName: dayNames[i],
        dayNumber: date.getDate(),
        isToday: date.getTime() === today.getTime(),
        isWeekend: i === 5 || i === 6,
      };
    });
  }, [weekStart, dayNames]);

  // Calculate appointment layouts (positions for overlapping appointments)
  const appointmentLayouts = useMemo(() => {
    const layouts = new Map<
      number,
      {
        position: AppointmentPosition;
        verticalOffset: number;
        height: number;
        containingSlot: string;
      }
    >();

    weekDays.forEach((day) => {
      const dayAppointments = weeklyAppointments.filter(
        (apt) => apt.date === day.date,
      );
      const overlapGroups = detectOverlaps(dayAppointments);

      overlapGroups.forEach((group) => {
        const positions = calculateHorizontalLayout(group);

        positions.forEach((pos) => {
          const appointment = dayAppointments.find(
            (apt) => apt.id === pos.appointmentId,
          );
          if (!appointment) return;

          const startTime = appointment.time || "07:00";
          const containingSlot = getContainingHourSlot(startTime);
          const [hourStr] = containingSlot.split(":");
          const isExpanded = expandedHours[hourStr] || false;

          const verticalOffset = calculateVerticalOffset(
            startTime,
            containingSlot,
            isExpanded,
          );
          const height = calculateHeight(
            startTime,
            appointment.duration || 45,
            expandedHours,
          );

          layouts.set(pos.appointmentId, {
            position: pos,
            verticalOffset,
            height,
            containingSlot,
          });
        });
      });
    });

    return layouts;
  }, [weeklyAppointments, weekDays, expandedHours]);

  const handleAppointmentClick = (appointment: IAppointment) => {
    setSelectedAppointment(appointment);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedAppointment(null);
  };

  const handleWeekChange = (direction: "prev" | "next") => {
    const newStart = new Date(weekStart);
    newStart.setDate(newStart.getDate() + (direction === "next" ? 7 : -7));
    setWeekStart(newStart);
  };

  const formatWeekRange = (): string => {
    const endDate = new Date(weekStart);
    endDate.setDate(endDate.getDate() + 6);

    const startMonth = weekStart.getMonth();
    const endMonth = endDate.getMonth();
    const startYear = weekStart.getFullYear();
    const endYear = endDate.getFullYear();

    const locale = getLocale();

    if (startMonth === endMonth && startYear === endYear) {
      const monthYear = weekStart.toLocaleDateString(locale, {
        month: "long",
        year: "numeric",
      });
      return `${weekStart.getDate()} - ${endDate.getDate()} ${monthYear}`;
    }

    if (startYear === endYear) {
      const startMonthName = weekStart.toLocaleDateString(locale, {
        month: "long",
      });
      const endMonthName = endDate.toLocaleDateString(locale, {
        month: "long",
      });
      return `${weekStart.getDate()} ${startMonthName} - ${endDate.getDate()} ${endMonthName} ${endYear}`;
    }

    const startFull = weekStart.toLocaleDateString(locale, {
      day: "numeric",
      month: "long",
      year: "numeric",
    });
    const endFull = endDate.toLocaleDateString(locale, {
      day: "numeric",
      month: "long",
      year: "numeric",
    });
    return `${startFull} - ${endFull}`;
  };

  // Export payload (PDF/Excel) for the currently displayed, filtered week.
  const exportProps = useMemo(
    () =>
      buildAppointmentsExport(weeklyAppointments, {
        viewType: "weekly",
        rangeLabel: formatWeekRange(),
        filterSummary: buildSearchFilterSummary(searchTerm),
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [weeklyAppointments, weekStart, searchTerm],
  );

  return (
    <div>
      {/* Statistics */}
      <div className={styles.statsContainer}>
        <div className={styles.statCard}>
          <div className={styles.statValue}>{weeklyAppointments.length}</div>
          <div className={styles.statLabel}>
            {t("APPOINTMENTS_TITLE") || 'סה"כ פגישות'}
          </div>
        </div>
      </div>

      {/* Calendar Section */}
      <div className={styles.calendarSection}>
        <div className={styles.calendarHeader}>
          <h2 className={styles.calendarTitle} style={{ margin: 0 }}>
            {formatWeekRange()}
          </h2>
          <div className={styles.calendarNav}>
            <button
              className={styles.navBtn}
              onClick={() => handleWeekChange("next")}
              aria-label="Next"
            >
              ‹
            </button>
            <button
              className={styles.todayBtn}
              onClick={() => {
                const today = new Date();
                const day = today.getDay();
                const diff = today.getDate() - day;
                setWeekStart(new Date(today.setDate(diff)));
              }}
            >
              {t("APPOINTMENTS_TODAY")}
            </button>
            <span className={styles.currentDate}>
              {weekStart.toLocaleDateString(getLocale(), {
                month: "long",
                year: "numeric",
              })}
            </span>
            <button
              className={styles.navBtn}
              onClick={() => handleWeekChange("prev")}
              aria-label="Previous"
            >
              ›
            </button>
            <ExportTableButtons
              disabled={weeklyAppointments.length === 0}
              {...exportProps}
            />
          </div>
        </div>

        {/* Weekly Calendar Grid - Desktop */}
        <div className={styles.weeklyGridWrapper}>
          {" "}
          {/* Wrapper is now block, matching Therapist weeklyGridDesktop */}
          <div
            className={styles.scrollableContainer}
            style={{ paddingLeft: "5px" }}
          >
            {/* Sticky Header Row - MOVED INSIDE SCROLLABLE CONTAINER */}
            <div className={styles.stickyHeaderRow}>
              <div className={styles.weeklyCalendarHeader}>
                <div
                  className={styles.timeHeader}
                  style={{
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    userSelect: "none",
                    padding: "8px",
                  }}
                  onClick={toggleAllHours}
                >
                  {hourlySlots.every((slot) => {
                    const [hourStr] = slot.split(":");
                    return expandedHours[hourStr];
                  }) ? (
                    <svg
                      width="20"
                      height="20"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      style={{ transition: "transform 0.3s ease" }}
                    >
                      <polyline points="18 15 12 9 6 15"></polyline>
                      <polyline points="18 21 12 15 6 21"></polyline>
                    </svg>
                  ) : (
                    <svg
                      width="20"
                      height="20"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      style={{ transition: "transform 0.3s ease" }}
                    >
                      <polyline points="6 9 12 15 18 9"></polyline>
                      <polyline points="6 3 12 9 18 3"></polyline>
                    </svg>
                  )}
                </div>
                {weekDays.map((day) => (
                  <div
                    key={day.date}
                    className={`${styles.dayHeader} ${
                      day.isToday ? styles.dayHeaderToday : ""
                    }`}
                  >
                    <span className={styles.dayName}>{day.dayName}</span>
                    <span className={styles.dayDate}>{day.dayNumber}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Content Grid */}
            <div
              className={styles.weeklyCalendar}
              style={{ minWidth: "800px" }}
            >
              {/* Time Slots */}
              {hourlySlots.map((hourSlot) => {
                const [hourStr] = hourSlot.split(":");
                const isExpanded = expandedHours[hourStr];
                const subSlots = isExpanded ? getSubSlots(hourStr) : [];

                return (
                  <React.Fragment key={hourSlot}>
                    {/* Main Hour Row */}
                    <div
                      className={`${styles.timeHeader} ${styles.timeHeaderHour}`}
                    >
                      <button
                        onClick={() => toggleHour(hourStr)}
                        style={{
                          background: "none",
                          border: "none",
                          cursor: "pointer",
                          padding: "4px",
                          marginRight: "4px",
                          fontSize: "12px",
                        }}
                        title={isExpanded ? "Collapse" : "Expand"}
                      >
                        {isExpanded ? "▲" : "▼"}
                      </button>
                      {hourSlot}
                    </div>
                    {weekDays.map((day) => {
                      const dayAppointments = weeklyAppointments.filter(
                        (apt) => apt.date === day.date,
                      );
                      const slotAppointments = dayAppointments.filter((apt) => {
                        const layout = appointmentLayouts.get(apt.id);
                        return layout && layout.containingSlot === hourSlot;
                      });

                      const displayedAppointments = slotAppointments.slice(
                        0,
                        3,
                      );
                      const hasMore = slotAppointments.length > 3;

                      return (
                        <div
                          key={`${day.date}-${hourSlot}`}
                          className={styles.timeSlot}
                          style={{ position: "relative" }}
                        >
                          {canSelfBook && (
                            <button
                              className={styles.addAppointmentBtn}
                              onClick={(e) => {
                                e.stopPropagation();
                                setCreateModalDate(day.date);
                                setCreateModalTime(hourSlot);
                                setShowCreateModal(true);
                              }}
                            >
                              +
                            </button>
                          )}

                          {displayedAppointments.map((apt) => {
                            const fundingColor = getAppointmentColors(apt);
                            const layout = appointmentLayouts.get(apt.id);
                            const isGoogleEvent =
                              apt.source === "google_calendar";
                            const isOther = isOtherEventAppointment(apt, isGoogleEvent);

                            const inlineStyles: React.CSSProperties = {
                              borderRightColor: fundingColor.borderColor,
                              backgroundColor: fundingColor.backgroundColor,
                            };

                            if (layout && layout.containingSlot === hourSlot) {
                              inlineStyles.top = `${layout.verticalOffset}px`;
                              inlineStyles.height = `${layout.height}px`;

                              if (layout.position.totalColumns > 1) {
                                const widthPercent =
                                  98 / layout.position.totalColumns;
                                const leftPercent =
                                  layout.position.columnIndex * widthPercent +
                                  1;
                                inlineStyles.width = `${widthPercent}%`;
                                inlineStyles.left = `${leftPercent}%`;
                                inlineStyles.right = "auto";
                              } else {
                                inlineStyles.left = "4px";
                                inlineStyles.right = "4px";
                                inlineStyles.width = "auto";
                              }
                            } else if (!layout) {
                              inlineStyles.position = "relative";
                              inlineStyles.margin = "2px 0";
                            } else {
                              return null;
                            }

                            return (
                              <div
                                key={apt.id}
                                className={styles.weeklyAppointment}
                                style={inlineStyles}
                                data-tooltip={getAppointmentTooltip(apt)}
                                onClick={() => handleAppointmentClick(apt)}
                              >
                                {/* Primary Text: Therapist Name / Comments / Group + Therapist */}
                                <div
                                  className={styles.weeklyAppointmentPatient}
                                >
                                  {isGoogleEvent ? "📅 " : isOther ? "📝 " : ""}
                                  {isOther
                                    ? truncateText(
                                        apt.comments || t("OTHER") || "אחר",
                                      )
                                    : apt.treatmentGroupName
                                      ? truncateText(
                                          `${apt.treatmentGroupName} - ${getAppointmentOwnerName(apt)}`,
                                        )
                                      : truncateText(getAppointmentOwnerName(apt))}
                                </div>
                                {/* Secondary Text (Therapist) - Hidden or Empty as per req, or used for extra info */}
                                <div
                                  className={styles.weeklyAppointmentTherapist}
                                >
                                  {/* Leaving empty as primary info covers it, or could duplicate therapist name if needed 
                                        but user said "at the beginning of the slot". */}
                                </div>
                              </div>
                            );
                          })}

                          {hasMore && (
                            <div
                              className={styles.moreAppointments}
                              onClick={(e) => {
                                e.stopPropagation();
                                setSelectedTimeSlot({
                                  date: day.date,
                                  time: hourSlot,
                                  appointments: slotAppointments,
                                });
                                setShowTimeSlotModal(true);
                              }}
                            >
                              +{slotAppointments.length - 3}{" "}
                              {t("APPOINTMENTS_MORE")}
                            </div>
                          )}
                        </div>
                      );
                    })}

                    {/* Sub-Slots (15, 30, 45) - Only if expanded */}
                    {subSlots.map((subSlot) => (
                      <React.Fragment key={subSlot}>
                        <div
                          className={`${styles.timeHeader} ${styles.timeHeaderQuarter}`}
                        >
                          {subSlot}
                        </div>
                        {weekDays.map((day) => {
                          const dayAppointments = weeklyAppointments.filter(
                            (apt) => apt.date === day.date,
                          );
                          const slotAppointments = dayAppointments.filter(
                            (apt) => {
                              const layout = appointmentLayouts.get(apt.id);
                              return (
                                layout && layout.containingSlot === subSlot
                              );
                            },
                          );

                          const displayedAppointments = slotAppointments.slice(
                            0,
                            3,
                          );
                          const hasMore = slotAppointments.length > 3;

                          return (
                            <div
                              key={`${day.date}-${subSlot}`}
                              className={styles.timeSlot}
                              style={{ position: "relative" }}
                            >
                              {canSelfBook && (
                                <button
                                  className={styles.addAppointmentBtn}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setCreateModalDate(day.date);
                                    setCreateModalTime(subSlot);
                                    setShowCreateModal(true);
                                  }}
                                >
                                  +
                                </button>
                              )}
                              {displayedAppointments.map((apt) => {
                                const fundingColor = getAppointmentColors(apt);
                                const layout = appointmentLayouts.get(apt.id);
                                const isGoogleEvent =
                                  apt.source === "google_calendar";
                                const isOther = isOtherEventAppointment(apt, isGoogleEvent);

                                const inlineStyles: React.CSSProperties = {
                                  borderRightColor: fundingColor.borderColor,
                                  backgroundColor: fundingColor.backgroundColor,
                                };

                                if (
                                  layout &&
                                  layout.containingSlot === subSlot
                                ) {
                                  inlineStyles.top = `${layout.verticalOffset}px`;
                                  inlineStyles.height = `${layout.height}px`;

                                  if (layout.position.totalColumns > 1) {
                                    const widthPercent =
                                      98 / layout.position.totalColumns;
                                    const leftPercent =
                                      layout.position.columnIndex *
                                        widthPercent +
                                      1;
                                    inlineStyles.width = `${widthPercent}%`;
                                    inlineStyles.left = `${leftPercent}%`;
                                    inlineStyles.right = "auto";
                                  } else {
                                    inlineStyles.left = "4px";
                                    inlineStyles.right = "4px";
                                    inlineStyles.width = "auto";
                                  }
                                } else if (!layout) {
                                  inlineStyles.position = "relative";
                                } else {
                                  return null;
                                }

                                return (
                                  <div
                                    key={apt.id}
                                    className={styles.weeklyAppointment}
                                    style={inlineStyles}
                                    data-tooltip={getAppointmentTooltip(apt)}
                                    onClick={() => handleAppointmentClick(apt)}
                                  >
                                    <div
                                      className={
                                        styles.weeklyAppointmentPatient
                                      }
                                    >
                                      {isGoogleEvent
                                        ? "📅 "
                                        : isOther
                                          ? "📝 "
                                          : ""}
                                      {isOther
                                        ? truncateText(
                                            apt.comments || t("OTHER") || "אחר",
                                          )
                                        : apt.treatmentGroupName
                                          ? truncateText(
                                              `${apt.treatmentGroupName} - ${getAppointmentOwnerName(apt)}`,
                                            )
                                          : truncateText(getAppointmentOwnerName(apt))}
                                    </div>
                                    <div
                                      className={
                                        styles.weeklyAppointmentTherapist
                                      }
                                    >
                                      {/* Empty as per logic above */}
                                    </div>
                                  </div>
                                );
                              })}

                              {hasMore && (
                                <div
                                  className={styles.moreAppointments}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setSelectedTimeSlot({
                                      date: day.date,
                                      time: subSlot,
                                      appointments: slotAppointments,
                                    });
                                    setShowTimeSlotModal(true);
                                  }}
                                >
                                  +{slotAppointments.length - 3}{" "}
                                  {t("APPOINTMENTS_MORE")}
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </React.Fragment>
                    ))}
                  </React.Fragment>
                );
              })}
            </div>
          </div>
        </div>

        {/* Weekly Stacked View - Mobile */}
        <div className={styles.weeklyStackedMobile}>
          {weekDays.map((day) => {
            const dayAppointments = weeklyAppointments.filter(
              (apt) => apt.date === day.date,
            );
            return (
              <div
                key={day.date}
                className={`${styles.stackedDayCard} ${
                  day.isToday ? styles.stackedDayToday : ""
                }`}
              >
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                  className={styles.stackedDayHeader}
                >
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                    }}
                  >
                    <span className={styles.stackedDayName}>{day.dayName}</span>
                    <span className={styles.stackedDayDate}>
                      {day.dayNumber}
                    </span>
                  </div>
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                    }}
                  >
                    <span className={styles.stackedDayCount}>
                      {dayAppointments.length} {t("APPOINTMENTS_TITLE")}
                    </span>
                    {canSelfBook && (
                      <button
                        className={styles.addAppointmentBtn}
                        onClick={(e) => {
                          e.stopPropagation();
                          setCreateModalDate(day.date);
                          setCreateModalTime("09:00");
                          setShowCreateModal(true);
                        }}
                        style={{
                          width: "28px",
                          height: "28px",
                          fontSize: "16px",
                        }}
                      >
                        +
                      </button>
                    )}
                  </div>
                </div>
                <div className={styles.stackedAppointmentsList}>
                  {dayAppointments.map((apt) => {
                    const fundingColor = getAppointmentColors(apt);
                    const isGoogleEvent = apt.source === "google_calendar";
                    const isOther = isOtherEventAppointment(apt, isGoogleEvent);
                    return (
                      <div
                        key={apt.id}
                        className={styles.stackedAppointment}
                        style={{
                          borderRightColor: fundingColor.borderColor,
                          backgroundColor: fundingColor.backgroundColor,
                        }}
                        onClick={() => handleAppointmentClick(apt)}
                      >
                        <div className={styles.stackedAppointmentTime}>
                          {apt.time}
                        </div>
                        <div className={styles.stackedAppointmentDetails}>
                          <div className={styles.stackedAppointmentPatient}>
                            {isGoogleEvent ? "📅 " : isOther ? "📝 " : ""}
                            {apt.treatmentGroupName
                              ? truncateText(
                                  `${apt.treatmentGroupName} - ${getAppointmentOwnerName(apt)}`,
                                )
                              : isOther
                                ? truncateText(
                                    apt.comments || t("OTHER") || "אחר",
                                  )
                                : truncateText(getAppointmentOwnerName(apt))}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Modals */}
      {isModalOpen && selectedAppointment && (
        <AppointmentDetailsModal
          appointment={selectedAppointment}
          onClose={handleCloseModal}
        />
      )}

      {/* Time Slot Modal - Shows all appointments in a time slot */}
      {showTimeSlotModal && selectedTimeSlot && (
        <div
          className={styles.modalOverlay}
          onClick={() => setShowTimeSlotModal(false)}
        >
          <div
            className={styles.modalContent}
            onClick={(e) => e.stopPropagation()}
          >
            <h3>
              {selectedTimeSlot.date} - {selectedTimeSlot.time}
            </h3>
            <div className={styles.timeSlotAppointmentsList}>
              {selectedTimeSlot.appointments.map((apt) => {
                const fundingColor = getAppointmentColors(apt);
                const isGoogleEvent = apt.source === "google_calendar";
                const isOther = isOtherEventAppointment(apt, isGoogleEvent);
                return (
                  <div
                    key={apt.id}
                    className={styles.weeklyAppointment}
                    style={{
                      borderRightColor: fundingColor.borderColor,
                      backgroundColor: fundingColor.backgroundColor,
                      cursor: "pointer",
                      marginBottom: "8px",
                    }}
                    onClick={() => {
                      setShowTimeSlotModal(false);
                      handleAppointmentClick(apt);
                    }}
                  >
                    <div className={styles.weeklyAppointmentPatient}>
                      {isGoogleEvent ? "📅 " : isOther ? "📝 " : ""}
                      {apt.treatmentGroupName
                        ? truncateText(
                            `${apt.treatmentGroupName} - ${getAppointmentOwnerName(apt)}`,
                          )
                        : isOther
                          ? truncateText(apt.comments || t("OTHER") || "אחר")
                          : truncateText(getAppointmentOwnerName(apt))}
                    </div>
                  </div>
                );
              })}
            </div>
            <button
              onClick={() => setShowTimeSlotModal(false)}
              className={styles.closeButton}
            >
              {t("CLOSE")}
            </button>
          </div>
        </div>
      )}

      {/* Create Appointment Modal */}
      {showCreateModal && (
        <CreateAppointmentModal
          onClose={() => setShowCreateModal(false)}
          onSuccess={handleCreateSuccess}
          initialDate={createModalDate}
          initialTime={createModalTime}
        />
      )}
    </div>
  );
};

export default WeeklyView;
